/**
 * Standard Content Block for
 *
 * A full width component which contains content, an optional heading or image
 * which can be configured to display on the left or right with isMediaLeft
 */
import React from 'react';
import classnames from 'classnames';
import { Row, Col, Button } from 'react-bootstrap';
import { Media } from '../Media';
import '../styles/index.scss';
const defaultProps = {
  brand: 'law',
  heading: null,
  subHeading: null,
  mediaSrc: null,
  mediaAlt: '',
  mediaCaption: '',
  isMediaLeft: true,
  children: null,
  hasEmbeddedVideo: false,
  roundal: null,
  roundalBackgroundColour: 'red',
  roundalPosition: 'top-right',
  buttonLabel: '',
  buttonType: 'primary',
  url: '',
  buttonTitle: '',
  buttonDescription: '',
  entryTitle: ''
};

/**
 * Standard Content Block
 * A content block on the page containing heading, content and image
 *
 * @param {node} heading A React node containing the heading for the block
 * @param {node} subHeading A React node containing the sub heading for the block
 * @param {node} content A React node containing the content for the block
 * @param {string} brand The brand the block and media should be styled for, default 'law'
 * @param {string} mediaSrc The source image to use, optional if children is provided
 * @param {string} mediaAlt The image alt text
 * @param {string} mediaCaption The caption shown on top of the image
 * @param {boolean} isMediaLeft Whether the image is shown left or right of the text content
 * @param {node} children Anything this component wraps to replace the image
 * @param  {string} buttonLabel => The text displayed within the button
 * @param  {string} buttonType => The button variant, primary and secondary
 * @param  {string} url => The url which clicking the button will take the user
 * @param  {string} buttonTitle => The title displayed within the button
 * @param  {string} buttonDescription => The description displayed within the button
 * @param  {string} entryTitle => The entryTitle is used to find the specific section to increase font size
 */
const StandardContentBlock = _ref => {
  let {
    heading,
    subHeading,
    content,
    brand,
    mediaSrc,
    mediaAlt,
    mediaCaption,
    isMediaLeft,
    hasEmbeddedVideo,
    children,
    roundal,
    roundalBackgroundColour,
    roundalPosition,
    buttonLabel,
    buttonType,
    url,
    buttonTitle,
    buttonDescription,
    entryTitle
  } = _ref;
  const textColumnOrder = isMediaLeft ? 1 : 0;
  const xsTextColumnStyling = {
    order: 1
  };
  const mdTextColumnStyling = {
    order: textColumnOrder
  };
  const videoStyling = classnames({
    'embed-responsive embed-responsive-16by9': hasEmbeddedVideo
  });
  const buttonStyling = classnames({
    'btn outline': true,
    'btn-primary': buttonType === "Primary",
    'btn-secondary': buttonType === "Secondary"
  });
  const sectionStyling = classnames({
    'content-block-section-text': true,
    'hasButtonDescription': url
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, heading && /*#__PURE__*/React.createElement("div", {
    "data-testid": "content-block-title",
    className: "mb-5"
  }, heading), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    "data-testid": "content-block-section-text",
    className: sectionStyling,
    xs: xsTextColumnStyling,
    md: mdTextColumnStyling
  }, subHeading && /*#__PURE__*/React.createElement("div", {
    "data-testid": "content-block-sub-title",
    className: "mb-5 content-block-sub-title"
  }, subHeading), content, url && /*#__PURE__*/React.createElement("div", {
    className: "d-flex content-block-button-text-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "content-block-button-description-container"
  }, /*#__PURE__*/React.createElement("p", {
    className: "content-block-button-description-heading"
  }, buttonTitle), /*#__PURE__*/React.createElement("p", {
    className: "content-block-button-description-subheading"
  }, buttonDescription)), /*#__PURE__*/React.createElement("div", {
    className: "content-block-button-info-container"
  }, /*#__PURE__*/React.createElement("a", {
    brand: brand,
    className: buttonStyling,
    href: url
  }, buttonLabel)))), (mediaSrc || children) && /*#__PURE__*/React.createElement(Col, {
    className: "pb-4 pb-md-0",
    xs: 12,
    md: 6
  }, children ? /*#__PURE__*/React.createElement("div", {
    className: `position-relative ${videoStyling}`
  }, children, roundal && /*#__PURE__*/React.createElement("div", {
    className: `roundal ${roundalBackgroundColour} ${roundalPosition}`
  }, roundal)) : /*#__PURE__*/React.createElement("div", {
    className: "position-relative"
  }, /*#__PURE__*/React.createElement(Media, {
    src: mediaSrc,
    alt: mediaAlt,
    caption: mediaCaption,
    brand: brand
  }), roundal && /*#__PURE__*/React.createElement("div", {
    className: `roundal ${roundalBackgroundColour} ${roundalPosition}`
  }, roundal)))));
};
StandardContentBlock.defaultProps = defaultProps;
export { StandardContentBlock };